import { Fragment, ReactNode } from 'react';

interface SupportMessageBodyProps {
    body: string;
}

type Block =
    | { kind: 'paragraph'; lines: string[] }
    | { kind: 'ul'; items: string[] }
    | { kind: 'ol'; start: number; items: string[] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;
// **gras** ou *italique*. Pas de lookbehind (Safari < 16.4 refuse la regex et casserait tout le bundle) :
// les bords de l'italique (« a * b », « a*b* ») sont vérifiés dans renderInline.
const INLINE = /\*\*([^*\n]+?)\*\*|\*([^*\s][^*\n]*?)\*/g;
const WORD_OR_STAR = /[\w*]/;

/** Découpe le texte en paragraphes et listes ; une ligne vide termine un bloc. */
function parseBlocks(body: string): Block[] {
    const blocks: Block[] = [];
    let current: Block | null = null;

    for (const rawLine of body.split(/\r?\n/)) {
        const bullet = BULLET.exec(rawLine);
        const numbered = NUMBERED.exec(rawLine);
        if (bullet) {
            if (current?.kind !== 'ul') blocks.push((current = { kind: 'ul', items: [] }));
            current.items.push(bullet[1]);
        } else if (numbered) {
            if (current?.kind !== 'ol') blocks.push((current = { kind: 'ol', start: Number(numbered[1]), items: [] }));
            current.items.push(numbered[2]);
        } else if (rawLine.trim() === '') {
            current = null;
        } else {
            const heading = HEADING.exec(rawLine);
            const line = heading ? `**${heading[1]}**` : rawLine;
            if (current?.kind !== 'paragraph') blocks.push((current = { kind: 'paragraph', lines: [] }));
            current.lines.push(line);
        }
    }

    return blocks;
}

/** Gras et italique en éléments React : le reste (dont toute balise HTML) reste du texte. */
function renderInline(text: string): ReactNode[] {
    const nodes: ReactNode[] = [];
    const re = new RegExp(INLINE.source, 'g');
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        const index = match.index;
        const end = index + match[0].length;
        if (match[2] !== undefined) {
            // Italique seulement si l'astérisque colle au mot des deux côtés : sinon c'est du texte.
            const before = index > 0 ? text[index - 1] : '';
            const after = end < text.length ? text[end] : '';
            if (WORD_OR_STAR.test(before) || WORD_OR_STAR.test(after) || /\s$/.test(match[2])) {
                re.lastIndex = index + 1;
                continue;
            }
        }
        if (index > last) nodes.push(text.slice(last, index));
        nodes.push(match[1] !== undefined
            ? <strong key={index} className="font-semibold">{match[1]}</strong>
            : <em key={index}>{match[2]}</em>);
        last = end;
    }
    if (last < text.length) nodes.push(text.slice(last));

    return nodes;
}

/**
 * Corps d'un message support (bulle hôtelier et inbox super admin).
 * Mise en forme minimale et sûre du texte produit par le modèle : listes, gras, italique.
 * Jamais de dangerouslySetInnerHTML : le corps vient d'un LLM.
 */
export default function SupportMessageBody({ body }: SupportMessageBodyProps) {
    return (
        <div className="space-y-2 break-words">
            {parseBlocks(body).map((block, i) => {
                if (block.kind === 'ul') {
                    return (
                        <ul key={i} className="list-disc space-y-1 pl-5">
                            {block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
                        </ul>
                    );
                }
                if (block.kind === 'ol') {
                    return (
                        <ol key={i} start={block.start} className="list-decimal space-y-1 pl-5">
                            {block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
                        </ol>
                    );
                }
                return (
                    <p key={i}>
                        {block.lines.map((line, j) => (
                            <Fragment key={j}>
                                {j > 0 && <br />}
                                {renderInline(line)}
                            </Fragment>
                        ))}
                    </p>
                );
            })}
        </div>
    );
}
